/**
 * Sentry client helpers.
 * Keep this module SDK-free so astro.config and unit tests can import it.
 *
 * The official `@sentry/astro` integration injects `sentry.client.config.ts`
 * only when a DSN is present. This file owns DSN/environment/sample-rate
 * rules and privacy-aware breadcrumb/event sanitization.
 */

export const SENTRY_PRODUCTION_TRACES_SAMPLE_RATE = 0.15;
export const SENTRY_NON_PRODUCTION_TRACES_SAMPLE_RATE = 0.1;

export interface SentryBreadcrumbLike {
  category?: string;
  type?: string;
  message?: string;
  data?: Record<string, unknown>;
}

export interface SentryEventLike {
  request?: {
    url?: string;
    query_string?: unknown;
    headers?: Record<string, string>;
    data?: unknown;
  };
}

export function getSentryDsn(
  dsn: string | undefined | null,
): string | undefined {
  if (typeof dsn !== 'string') {
    return undefined;
  }
  const trimmed = dsn.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

export function isSentryDsnConfigured(dsn: string | undefined | null): boolean {
  return getSentryDsn(dsn) !== undefined;
}

export function getSentryEnvironment(
  explicit: string | undefined | null,
  isProdBuild: boolean,
): string {
  if (typeof explicit === 'string' && explicit.trim()) {
    return explicit.trim();
  }
  return isProdBuild ? 'production' : 'development';
}

export function getSentryTracesSampleRate(environment: string): number {
  return environment === 'production'
    ? SENTRY_PRODUCTION_TRACES_SAMPLE_RATE
    : SENTRY_NON_PRODUCTION_TRACES_SAMPLE_RATE;
}

/** Drop query strings and fragments so coords, usernames, and share params stay out of Sentry. */
export function stripUrlQueryAndHash(url: string): string {
  return url.split('#')[0].split('?')[0];
}

export function sanitizeSentryBreadcrumb<T extends SentryBreadcrumbLike>(
  breadcrumb: T,
): T | null {
  const next: T = { ...breadcrumb };

  if (next.data) {
    const data: Record<string, unknown> = { ...next.data };
    for (const key of ['url', 'to', 'from']) {
      const value = data[key];
      if (typeof value === 'string') {
        data[key] = stripUrlQueryAndHash(value);
      }
    }
    delete data.body;
    delete data.request_body;
    next.data = data;
  }

  if (typeof next.message === 'string') {
    next.message = stripUrlQueryAndHash(next.message);
  }

  return next;
}

export function sanitizeSentryEvent<T extends SentryEventLike>(event: T): T {
  if (!event.request) {
    return event;
  }

  const request = { ...event.request };
  if (typeof request.url === 'string') {
    request.url = stripUrlQueryAndHash(request.url);
  }
  delete request.query_string;
  delete request.data;
  return { ...event, request };
}
